import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { AIMWindow } from './AIMWindow';
import { AIMButton } from './AIMButton';
import { AIMInput } from './AIMInput';
import { useUserStatus, STATUS_CONFIG, type UserStatus } from '@/hooks/useUserStatus';
import { cn } from '@/lib/utils';

interface StatusSelectorProps {
  onClose: () => void;
}

const STATUS_OPTIONS: UserStatus[] = [
  'available',
  'away',
  'be-right-back',
  'busy',
  'on-the-phone',
  'out-to-lunch',
  'invisible',
  'custom',
];

// Classic AIM away messages
const PRESET_AWAY_MESSAGES = [
  "I am away from my computer right now.",
  "I am currently away from the computer.",
  "I stepped out. Be right back.",
  "Gone for a bit. Back soon!",
  "Out to lunch. Will return shortly.",
  "On the phone. Leave a message!",
  "In a meeting. BRB.",
  "Taking a break.",
  "Do not disturb.",
  "AFK - Away From Keyboard",
];

export function StatusSelector({ onClose }: StatusSelectorProps) {
  const { status, customMessage, setStatus, setCustomMessage } = useUserStatus();
  const [selectedStatus, setSelectedStatus] = useState<UserStatus>(status);
  const [message, setMessage] = useState(customMessage);
  const [showPresets, setShowPresets] = useState(false);

  const handleSave = () => {
    setStatus(selectedStatus, message);
    onClose();
  };

  const handleSelectPreset = (preset: string) => {
    setMessage(preset);
    setShowPresets(false);
  };

  const isAwayStatus = ['away', 'be-right-back', 'busy', 'on-the-phone', 'out-to-lunch', 'custom'].includes(selectedStatus);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <AIMWindow
        title="Set Your Status"
        onClose={onClose}
        className="w-[350px]"
        showMinimize={false}
      >
        <div className="p-4 space-y-4">
          {/* Status Options */}
          <div className="space-y-1">
            <label className="text-xs font-bold">Select Status:</label>
            <div className="aim-window-inset bg-white dark:bg-[hsl(220,15%,12%)] p-1 max-h-[200px] overflow-y-auto">
              {STATUS_OPTIONS.map((statusOption) => {
                const config = STATUS_CONFIG[statusOption];
                const isSelected = selectedStatus === statusOption;
                
                return (
                  <div
                    key={statusOption}
                    onClick={() => setSelectedStatus(statusOption)}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1.5 cursor-pointer text-xs",
                      isSelected 
                        ? "bg-[hsl(var(--aim-blue))] text-white" 
                        : "hover:bg-[hsl(var(--aim-lightblue))] dark:hover:bg-[hsl(var(--aim-darkblue))]"
                    )}
                  >
                    <span>{config.icon}</span>
                    <span className="flex-1">{config.label}</span>
                    {isSelected && <Check className="w-3 h-3" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Away Message (shown for away-type statuses) */}
          {isAwayStatus && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold">
                  {selectedStatus === 'custom' ? 'Custom Status:' : 'Away Message:'}
                </label>
                <div className="relative">
                  <button
                    onClick={() => setShowPresets(!showPresets)}
                    className="aim-button px-2 py-0.5 text-[10px] flex items-center gap-1"
                  >
                    Presets
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  
                  {showPresets && (
                    <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-[hsl(220,15%,18%)] border-2 border-[hsl(var(--border))] shadow-lg z-10">
                      <div className="max-h-[150px] overflow-y-auto">
                        {PRESET_AWAY_MESSAGES.map((preset, index) => (
                          <div
                            key={index}
                            onClick={() => handleSelectPreset(preset)}
                            className="px-2 py-1 text-[11px] cursor-pointer hover:bg-[hsl(var(--aim-lightblue))] dark:hover:bg-[hsl(var(--aim-darkblue))] truncate"
                          >
                            {preset}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={selectedStatus === 'custom' ? "Enter your custom status..." : "Enter your away message..."}
                className="aim-input w-full min-h-[60px] resize-none text-xs"
                maxLength={200}
              />
              <div className="text-[10px] text-[hsl(var(--muted-foreground))] text-right">
                {message.length}/200
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="bg-[hsl(var(--muted))] p-2 border border-[hsl(var(--border))]">
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mb-1">Preview:</div>
            <div className="flex items-center gap-2 text-xs">
              <span>{STATUS_CONFIG[selectedStatus].icon}</span>
              <span className={STATUS_CONFIG[selectedStatus].color}>
                {selectedStatus === 'custom' && message 
                  ? message 
                  : STATUS_CONFIG[selectedStatus].label}
              </span>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2">
            <AIMButton onClick={onClose}>
              Cancel
            </AIMButton>
            <AIMButton variant="primary" onClick={handleSave}>
              OK
            </AIMButton>
          </div>
        </div>
      </AIMWindow>
    </div>
  );
}

// Compact status display for the buddy list
interface StatusDisplayProps {
  onClick?: () => void;
}

export function StatusDisplay({ onClick }: StatusDisplayProps) {
  const { status, customMessage, statusConfig } = useUserStatus();
  
  const displayText = status === 'custom' && customMessage 
    ? customMessage 
    : statusConfig.label;

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 text-[10px] hover:underline cursor-pointer w-full text-left"
      title="Click to change status"
    >
      <span>{statusConfig.icon}</span>
      <span className={cn("truncate", statusConfig.color)}>
        {displayText}
      </span>
      <ChevronDown className="w-2 h-2 flex-shrink-0 opacity-50" />
    </button>
  );
}
